import { DOC_TYPE_FORM_TYPES, buildDefaultDocTypeRules } from "../../../util/docTypeRuleDefaults";
import { DOC_TYPE_SCOPE, resolveDocTypeMeta, splitDocTypesByScope } from "../../../util/docTypeRuleMeta";
import { evaluateDocTypeRules, fetchDocTypeRules, formatIndividualName } from "../../../util/docTypeRuleEvaluator";
import { doGET } from "../../../util/HttpUtil";
import { getBusinessReviewPermissions, getPrimaryIndividualIndex } from "./BusinessPeriodicReview/helper";
import { FORM_TYPE } from "./IPlushelper";

export const SUBMIT_DOC_FLOW = {
  INDIVIDUAL_PLUS_ONBOARDING: "INDIVIDUAL_PLUS_ONBOARDING",
  INDIVIDUAL_PERIODIC_REVIEW: "INDIVIDUAL_PERIODIC_REVIEW",
  BUSINESS_PLUS_ONBOARDING: "BUSINESS_PLUS_ONBOARDING",
  BUSINESS_PRIME_APPLICATION: "BUSINESS_PRIME_APPLICATION",
  BUSINESS_PERIODIC_REVIEW: "BUSINESS_PERIODIC_REVIEW",
  BUSINESS_V2_ONBOARDING: "BUSINESS_V2_ONBOARDING",
};

const fetchDocTypeMap = async () => {
  const response = await doGET("/api/doc-type/grid?rows=-1");
  const rows = response?.data?.rows || [];
  return rows.reduce((acc, row) => {
    const key = row?.key || row?._id;
    if (key) {
      acc[key] = row;
    }
    return acc;
  }, {});
};

const getMeta = (docTypeMap, docType, context = {}) =>
  resolveDocTypeMeta(docType, { context, docTypeMap });

const getRuleBundle = async ({ formType, applicationData, useDefaultFallback = false }) => {
  let rules = await fetchDocTypeRules(formType);
  if ((!rules || !rules.length) && useDefaultFallback) {
    rules = buildDefaultDocTypeRules().filter((rule) => rule.formType === formType);
  }

  return {
    rules,
    docTypes: evaluateDocTypeRules(rules, applicationData) || [],
  };
};

const checkIsDocumentUploaded = async ({ recordId, uboId, type }) => {
  if (!recordId || !type) return false;
  const response = await doGET(
    `/api/application-doc/grid?rows=-1&form_id=${recordId}${uboId ? `&ubo_id=${uboId}` : ""}&type=${type}`,
  );

  return Boolean(response?.data?.rows?.[0]?.attachment?.urls?.length);
};

const mergeDocDescriptors = (...groups) => {
  const merged = new Map();

  groups.flat().forEach((entry) => {
    if (!entry) return;
    const type = typeof entry === "string" ? entry : entry?.type;
    if (!type) return;
    merged.set(type, entry);
  });

  return Array.from(merged.values());
};

const normalizeBusinessDescriptor = (descriptor, docTypeMap) => {
  if (!descriptor) return null;

  if (typeof descriptor === "string") {
    return getMeta(docTypeMap, descriptor, { scope: DOC_TYPE_SCOPE.BUSINESS });
  }

  const type = descriptor?.type || descriptor?.key || descriptor?._id;
  if (!type) return null;

  const meta = getMeta(docTypeMap, type, { scope: DOC_TYPE_SCOPE.BUSINESS });
  return {
    ...meta,
    ...descriptor,
    type,
    field: descriptor?.field || meta.field,
    showCategory: descriptor?.showCategory || meta.showCategory,
    label: descriptor?.label || meta.label,
    errorMessage: descriptor?.errorMessage || descriptor?.label || meta.errorMessage,
  };
};

const mapDynamicUboDocs = (docMap = {}, individuals = [], reasons = {}, extraDocTypes = []) => {
  const individualsById = (individuals || []).reduce((acc, individual) => {
    if (individual?._id) {
      acc[individual._id] = individual;
    }
    return acc;
  }, {});

  return Object.entries(docMap || {})
    .map(([uboId, docTypes]) => {
      const individual = individualsById[uboId];
      if (!individual) return null;

      const combinedDocTypes = Array.from(
        new Set([...(Array.isArray(docTypes) ? docTypes : []), ...(extraDocTypes || [])]),
      );

      if (!combinedDocTypes.length) return null;

      return {
        uboId,
        individual,
        displayName: formatIndividualName(individual),
        reason: reasons?.[uboId],
        docTypes: combinedDocTypes,
      };
    })
    .filter(Boolean);
};

const collectIndividualOnboardingRequirements = async ({ data }) => {
  const ruleBundle = await getRuleBundle({
    formType: DOC_TYPE_FORM_TYPES.INDIVIDUAL_PLUS,
    applicationData: data,
    useDefaultFallback: true,
  });

  const docTypes = ruleBundle.docTypes?.length ? ruleBundle.docTypes : ["ProofOfAddress"];

  return {
    businessDocs: [],
    individualRequirements: docTypes.length
      ? [
        {
          uboId: data?.ubo_id,
          individual: data,
          displayName: formatIndividualName(data),
          docTypes,
        },
      ]
      : [],
  };
};

const collectIndividualPeriodicReviewRequirements = async ({ recordId, data, formType }) => {
  const docTypeFormType =
    formType === FORM_TYPE.IndividualPrime
      ? DOC_TYPE_FORM_TYPES.INDIVIDUAL_PRIME_PR
      : DOC_TYPE_FORM_TYPES.INDIVIDUAL_PLUS_PR;

  const [eddResponse, ruleBundle] = await Promise.all([
    doGET(`/api/individual-lite/edd-info?id=${recordId}`),
    getRuleBundle({
      formType: docTypeFormType,
      applicationData: { ...data, formType },
      useDefaultFallback: false,
    }),
  ]);

  const eddDocTypes = Array.isArray(eddResponse?.data?.toCaptureDocs?.[data?.ubo_id])
    ? eddResponse.data.toCaptureDocs[data.ubo_id]
    : [];
  const docTypes = Array.from(new Set([...(eddDocTypes || []), ...(ruleBundle.docTypes || [])]));

  return {
    businessDocs: [],
    individualRequirements: docTypes.length
      ? [
        {
          uboId: data?.ubo_id,
          individual: data,
          displayName: formatIndividualName(data),
          docTypes,
        },
      ]
      : [],
  };
};

const collectBusinessPlusOnboardingRequirements = async ({ recordId, data, docTypeMap }) => {
  const ruleBundle = await getRuleBundle({
    formType: DOC_TYPE_FORM_TYPES.BUSINESS_PLUS,
    applicationData: data,
    useDefaultFallback: true,
  });

  const scopedRuleDocTypes = splitDocTypesByScope(ruleBundle.docTypes || [], docTypeMap);
  const businessDocs = mergeDocDescriptors(scopedRuleDocTypes.business || []);
  const individuals = Array.isArray(data?.individuals) ? data.individuals : [];
  const individualRequirements = individuals
    .map((individual) => {
      const matchedDocTypes = evaluateDocTypeRules(ruleBundle.rules || [], {
        ...data,
        individuals: [individual],
      }).filter((docType) => getMeta(docTypeMap, docType).scope === DOC_TYPE_SCOPE.INDIVIDUAL);

      if (!matchedDocTypes.length) return null;

      return {
        uboId: individual?._id,
        individual,
        displayName: formatIndividualName(individual),
        docTypes: matchedDocTypes,
      };
    })
    .filter(Boolean);

  return {
    businessDocs,
    individualRequirements,
  };
};

const collectBusinessPeriodicCoreRequirements = async ({ recordId, data, applicationForm, docTypeMap }) => {
  const [eddInfoResponse, ruleBundle] = await Promise.all([
    doGET(`/api/business/edd-info?id=${recordId}`),
    getRuleBundle({
      formType: DOC_TYPE_FORM_TYPES.BUSINESS_PLUS_PR,
      applicationData: { ...data, applicationForm },
      useDefaultFallback: false,
    }),
  ]);

  const scopedRuleDocTypes = splitDocTypesByScope(ruleBundle.docTypes || [], docTypeMap);
  const toCaptureDocs = eddInfoResponse?.data?.toCaptureDocs;
  const businessDocsFromEdd = Array.isArray(toCaptureDocs?.BUSINESS) ? toCaptureDocs.BUSINESS : [];
  const businessDocs = mergeDocDescriptors(businessDocsFromEdd, scopedRuleDocTypes.business || []);

  const permissions = getBusinessReviewPermissions ? getBusinessReviewPermissions() : {};
  const individuals = Array.isArray(data?.individuals) ? data.individuals : [];
  const allowedIndividuals = permissions?.allowAdditionalIndividuals
    ? individuals
    : (() => {
      const primaryIndex = getPrimaryIndividualIndex(data);
      if (primaryIndex < 0 || !individuals[primaryIndex]) return [];
      return [individuals[primaryIndex]];
    })();

  const dynamicUboDocMap = toCaptureDocs
    ? Object.entries(toCaptureDocs).reduce((acc, [key, value]) => {
      if (key !== "BUSINESS") {
        acc[key] = value;
      }
      return acc;
    }, {})
    : null;

  const ruleIndividualDocTypes = scopedRuleDocTypes.individual || [];
  const individualRequirements =
    dynamicUboDocMap !== null && dynamicUboDocMap !== undefined
      ? mapDynamicUboDocs(
        dynamicUboDocMap,
        allowedIndividuals,
        eddInfoResponse?.data?.uboReasonsForKYC || {},
        ruleIndividualDocTypes,
      )
      : ruleIndividualDocTypes.length
        ? allowedIndividuals.map((individual) => ({
          uboId: individual?._id,
          individual,
          displayName: formatIndividualName(individual),
          docTypes: ruleIndividualDocTypes,
        }))
        : [];

  return {
    businessDocs,
    individualRequirements,
  };
};

const collectBusinessPrimeApplicationRequirements = async ({ data, applicationForm }) => {
  const docTypeFormType = applicationForm?.connectedApplication
    ? DOC_TYPE_FORM_TYPES.BUSINESS_PRIME
    : DOC_TYPE_FORM_TYPES.BUSINESS_PRIME_UPGRADE;

  const ruleBundle = await getRuleBundle({
    formType: docTypeFormType,
    applicationData: { ...data, applicationForm },
    useDefaultFallback: true,
  });

  return {
    businessDocs: ruleBundle.docTypes || [],
    individualRequirements: [],
  };
};

const collectBusinessPeriodicPrimeAdditionalRequirements = async ({ data, applicationForm }) => {
  const ruleBundle = await getRuleBundle({
    formType: DOC_TYPE_FORM_TYPES.BUSINESS_PRIME_PR,
    applicationData: { ...data, applicationForm },
    useDefaultFallback: true,
  });

  return {
    businessDocs: ruleBundle.docTypes || [],
    individualRequirements: [],
  };
};

const validateDescriptors = async ({ recordId, businessDocs, individualRequirements, docTypeMap }) => {
  const errors = [];

  for (const descriptor of businessDocs || []) {
    const meta = normalizeBusinessDescriptor(descriptor, docTypeMap);
    if (!meta?.type) continue;

    const isUploaded = await checkIsDocumentUploaded({ recordId, type: meta.type });
    if (!isUploaded) {
      errors.push({
        type: meta.type,
        error: meta.errorMessage || meta.label || "Please upload the required documents.",
      });
    }
  }

  for (const entry of individualRequirements || []) {
    const displayName = entry?.displayName || formatIndividualName(entry?.individual);
    for (const docType of entry?.docTypes || []) {
      const meta = getMeta(docTypeMap, docType, {
        scope: DOC_TYPE_SCOPE.INDIVIDUAL,
        individual: entry?.individual,
        displayName,
      });
      const isUploaded = await checkIsDocumentUploaded({
        recordId,
        uboId: entry?.uboId,
        type: meta.type,
      });

      if (!isUploaded) {
        errors.push({
          type: docType,
          ubo_id: entry?.uboId,
          error: meta.errorMessage || meta.label || "Please upload the required documents.",
        });
      }
    }
  }

  return {
    isValid: errors.length === 0,
    errors,
  };
};

const collectBusinessV2OnboardingRequirements = async ({ data, applicationForm, docTypeMap }) => {
  const v2Type = Number(applicationForm?.type);

  // Determine which doc rule keys to evaluate based on V2 subtype
  let docRuleFormTypes = [];
  if (v2Type === 1) {
    docRuleFormTypes = [DOC_TYPE_FORM_TYPES.BUSINESS_V2_PLUS];
  } else if (v2Type === 2) {
    // Type 2 (Prime single application) = union of Plus + Prime requirements
    docRuleFormTypes = [DOC_TYPE_FORM_TYPES.BUSINESS_V2_PLUS, DOC_TYPE_FORM_TYPES.BUSINESS_V2_PRIME];
  } else if (v2Type === 3) {
    docRuleFormTypes = [DOC_TYPE_FORM_TYPES.BUSINESS_V2_UPGRADE];
  }

  // Fetch rules for all applicable form types and merge
  const allRuleBundles = await Promise.all(
    docRuleFormTypes.map((ft) =>
      getRuleBundle({
        formType: ft,
        applicationData: data,
        useDefaultFallback: true,
      })
    )
  );

  // Merge all scoped doc types from all rule bundles
  const allBusinessDocs = [];
  const allRules = [];
  for (const bundle of allRuleBundles) {
    const scoped = splitDocTypesByScope(bundle.docTypes || [], docTypeMap);
    allBusinessDocs.push(...(scoped.business || []));
    allRules.push(...(bundle.rules || []));
  }

  const businessDocs = mergeDocDescriptors(...allBusinessDocs.map((d) => [d]));

  // Evaluate individual-scope doc requirements
  const individuals = Array.isArray(data?.individuals) ? data.individuals : [];
  const individualRequirements = individuals
    .map((individual) => {
      const matchedDocTypes = evaluateDocTypeRules(allRules, {
        ...data,
        individuals: [individual],
      }).filter((docType) => getMeta(docTypeMap, docType).scope === DOC_TYPE_SCOPE.INDIVIDUAL);

      if (!matchedDocTypes.length) return null;

      return {
        uboId: individual?._id,
        individual,
        displayName: formatIndividualName(individual),
        docTypes: matchedDocTypes,
      };
    })
    .filter(Boolean);

  return {
    businessDocs,
    individualRequirements,
  };
};

export const validateSubmitDocuments = async ({
  flow,
  recordId,
  data,
  applicationForm,
  formType,
}) => {
  const docTypeMap = await fetchDocTypeMap();
  let businessDocs = [];
  let individualRequirements = [];

  if (flow === SUBMIT_DOC_FLOW.INDIVIDUAL_PLUS_ONBOARDING) {
    const requirements = await collectIndividualOnboardingRequirements({
      data,
    });
    businessDocs = requirements.businessDocs;
    individualRequirements = requirements.individualRequirements;
  } else if (flow === SUBMIT_DOC_FLOW.INDIVIDUAL_PERIODIC_REVIEW) {
    const requirements = await collectIndividualPeriodicReviewRequirements({
      recordId,
      data,
      formType,
    });
    businessDocs = requirements.businessDocs;
    individualRequirements = requirements.individualRequirements;
  } else if (flow === SUBMIT_DOC_FLOW.BUSINESS_PLUS_ONBOARDING) {
    const requirements = await collectBusinessPlusOnboardingRequirements({
      recordId,
      data,
      docTypeMap,
    });
    businessDocs = requirements.businessDocs;
    individualRequirements = requirements.individualRequirements;
  } else if (flow === SUBMIT_DOC_FLOW.BUSINESS_PRIME_APPLICATION) {
    const requirements = await collectBusinessPrimeApplicationRequirements({
      data,
      applicationForm,
    });
    businessDocs = requirements.businessDocs;
    individualRequirements = requirements.individualRequirements;
  } else if (flow === SUBMIT_DOC_FLOW.BUSINESS_V2_ONBOARDING) {
    const requirements = await collectBusinessV2OnboardingRequirements({
      recordId,
      data,
      applicationForm,
      docTypeMap,
    });
    businessDocs = requirements.businessDocs;
    individualRequirements = requirements.individualRequirements;
  } else if (flow === SUBMIT_DOC_FLOW.BUSINESS_PERIODIC_REVIEW) {
    const coreRequirements = await collectBusinessPeriodicCoreRequirements({
      recordId,
      data,
      applicationForm,
      docTypeMap,
    });

    businessDocs = coreRequirements.businessDocs;
    individualRequirements = coreRequirements.individualRequirements;

    if (applicationForm?.formType === FORM_TYPE.BusinessPrime) {
      const additionalRequirements = await collectBusinessPeriodicPrimeAdditionalRequirements({
        data,
        applicationForm,
      });
      businessDocs = mergeDocDescriptors(businessDocs, additionalRequirements.businessDocs);
    }
  }

  return validateDescriptors({
    recordId,
    businessDocs,
    individualRequirements,
    docTypeMap,
  });
};
